import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { generatePKCECodes, getAuthorizationUrl } from '../../utils/pkce';
import { logger } from '../../utils';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
      return;
    }

    const handleCallback = async () => {
      try {
        // Check if this is a callback with authorization code
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        
        if (code) {
          logger.info('AUTH', 'Processing authorization code callback');
          
          // Get stored PKCE verifier
          const verifier = sessionStorage.getItem('code_verifier');
          if (!verifier) {
            throw new Error('No code verifier found');
          }

          // Exchange code for tokens
          const tokenEndpoint = import.meta.env.VITE_WSO2_ENDPOINTS_Token;
          const clientId = import.meta.env.VITE_WSO2_CLIENT_ID;
          const redirectUri = import.meta.env.VITE_DEV_MODE === 'true'
            ? import.meta.env.VITE_AUTH_CALLBACK_URL_DEV
            : import.meta.env.VITE_AUTH_CALLBACK_URL_PROD;

          const tokenResponse = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'authorization_code',
              client_id: clientId,
              code_verifier: verifier,
              code: code,
              redirect_uri: redirectUri,
            }),
          });

          if (!tokenResponse.ok) {
            const error = await tokenResponse.json();
            logger.error('AUTH', 'Token exchange failed', { 
              status: tokenResponse.status,
              error,
              headers: Object.fromEntries(tokenResponse.headers.entries())
            });
            throw new Error(error.error_description || 'Failed to exchange code for tokens');
          }

          const tokens = await tokenResponse.json();
          logger.debug('AUTH', 'Token exchange response', { 
            accessToken: tokens.access_token?.substring(0, 20) + '...',
            tokenType: tokens.token_type,
            expiresIn: tokens.expires_in,
            scope: tokens.scope,
            idTokenLength: tokens.id_token?.length
          });

          // Clear PKCE verifier
          sessionStorage.removeItem('code_verifier');

          // Process login with tokens
          const accessToken = `${tokens.token_type} ${tokens.access_token}`;
          await login(accessToken, tokens.id_token);
          navigate('/admin/dashboard');
          return;
        }

        // If not a callback, initiate login
        const { verifier, challenge } = await generatePKCECodes();
        sessionStorage.setItem('code_verifier', verifier);
        const authUrl = getAuthorizationUrl(challenge);
        logger.info('AUTH', 'Redirecting to authorization URL');
        window.location.href = authUrl;
      } catch (error) {
        logger.error('AUTH', 'Failed to process login', { error });
      }
    };

    handleCallback();
  }, [isAuthenticated, navigate, location, login]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="p-8 bg-white rounded-lg shadow-md">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tedy-red mx-auto"></div>
        <p className="mt-4 text-gray-600">Processing login...</p>
      </div>
    </div>
  );
};

export default Login;
